import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Alert, Paper, Chip } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { PracticeQuestionManager } from '../../components/teacher/PracticeQuestionManager';
import { getModule, getModuleContent } from '../../services/lessonService';

/**
 * Page for managing practice questions for a specific unit
 * This page is focused solely on practice questions - content editing is handled in ManageUnits
 */
export const EditUnitContentPage: React.FC = () => {
  const { moduleId, unitId } = useParams<{ moduleId: string; unitId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unitTitle, setUnitTitle] = useState<string>('');
  const [moduleTitle, setModuleTitle] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [gradeLevel, setGradeLevel] = useState<string>('');

  useEffect(() => {
    loadUnitData();
  }, [moduleId, unitId]);

  const loadUnitData = async () => {
    if (!moduleId || !unitId) return;

    try {
      setLoading(true);

      // Get module metadata
      const moduleData = await getModule(moduleId);
      setModuleTitle(moduleData.title);
      setSubject(moduleData.subject);
      setGradeLevel(moduleData.grade_level);

      // Get unit details from module content
      const content = await getModuleContent(moduleId);
      const unit = content.units?.find((u: any) => u.unit_id === unitId);

      if (unit) {
        setUnitTitle(unit.title);
      } else {
        setError('Unit not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load unit data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/teacher/lessons/${moduleId}/units`)}
        >
          Back to Units
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header with back button */}
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate(`/teacher/lessons/${moduleId}/units`)}
        sx={{ mb: 2 }}
      >
        Back to Units
      </Button>

      {/* Page title and metadata */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Practice Questions
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {unitTitle}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Chip label={moduleTitle} size="small" variant="outlined" />
          <Chip label={subject} size="small" color="primary" />
          <Chip label={gradeLevel} size="small" color="secondary" />
        </Box>
      </Box>

      {/* Info alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Create practice questions for students to reinforce their learning. These are separate from
        the unit test and allow students to practice as much as they need.
      </Alert>

      {/* Practice Question Manager */}
      <Paper sx={{ p: 3 }}>
        {moduleId && unitId && (
          <PracticeQuestionManager moduleId={moduleId} unitId={unitId} unitTitle={unitTitle} />
        )}
      </Paper>
    </Box>
  );
};
